import {
  ORE_CASES,
  ORE_MEASUREMENTS,
  oreMembership,
  type OreScenario,
} from "../sim/ore";

export interface ExhibitOptions {
  scenario: OreScenario;
  p: "TRUE" | "FALSE" | "UNKNOWN";
  q: "TRUE" | "FALSE" | "UNKNOWN";
  metric: keyof typeof ORE_MEASUREMENTS.base;
}

export const DEFAULT_EXHIBIT_OPTIONS: ExhibitOptions = {
  scenario: "original",
  p: "TRUE",
  q: "TRUE",
  metric: "employeeCalls",
};

export interface ExhibitNode {
  title: string;
  detail: string;
  tag: string;
  tone: "blue" | "amber" | "teal" | "rose";
}

export interface OreExhibit {
  room: string;
  boundary: string;
  nodes: ExhibitNode[];
  links: [number, number][];
  focus: number[];
}

const number = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
const node = (
  title: string,
  detail: string,
  tag: string,
  tone: ExhibitNode["tone"] = "blue",
): ExhibitNode => ({ title, detail, tag, tone });

// A presentation of recorded cases, not an optimizer or a trace replay.
export function oreExhibit(
  id: string,
  options: ExhibitOptions,
): OreExhibit | null {
  const c = ORE_CASES[options.scenario];
  const common =
    "Spatial model · one server process · compilation, not data movement";
  switch (id) {
    case "ore-predicate":
      return {
        room: "Query transformer / predicate workshop",
        boundary: common,
        nodes: [
          node(
            "P OR Q",
            "One query block · original retained",
            "SQL EXPRESSION",
            "amber",
          ),
          node(
            "P · own assignment",
            "Belongs to the current employee",
            "LEFT PREDICATE",
          ),
          node(
            "Q · delegation",
            "Rules contain a nested AND",
            "RIGHT PREDICATE",
            "teal",
          ),
          node(
            "Correlated subquery",
            "Nested inside the delegation rules",
            "FOLLOW THE TREE",
            "teal",
          ),
        ],
        links: [
          [0, 1],
          [0, 2],
          [2, 3],
        ],
        focus: [0, 1, 2, 3],
      };
    case "ore-validity":
      return {
        room: "Query transformer / eligibility gates",
        boundary: common,
        nodes: [
          node("Check eligibility", "kkqoreCheckQBValidity", "01 · CHECK"),
          node(
            "Generate candidate",
            "Original state is still available",
            "02 · EXPAND",
            "teal",
          ),
          node(
            "Cost both states",
            "Eligible does not mean selected",
            "03 · COMPARE",
            "amber",
          ),
        ],
        links: [
          [0, 1],
          [1, 2],
        ],
        focus: [0, 1, 2],
      };
    case "ore-branches":
      return {
        room: "Plan atelier / building an ORE candidate",
        boundary: common,
        nodes: [
          node(
            "Original · P OR Q",
            "Retained for the cost comparison",
            "BASE CANDIDATE",
            "amber",
          ),
          node(
            "P · own assignment",
            "INDEX RANGE SCAN → TABLE BY ROWID",
            "BRANCH 1",
          ),
          node(
            "Q AND LNNVL(P)",
            "Delegation · TABLE ACCESS FULL",
            "BRANCH 2",
            "teal",
          ),
          node(
            "UNION ALL",
            "Two branches · same server process",
            "ORE CANDIDATE",
            "teal",
          ),
        ],
        links: [
          [0, 1],
          [0, 2],
          [1, 3],
          [2, 3],
        ],
        focus: [0, 1, 2, 3],
      };
    case "ore-guard": {
      const m = oreMembership(options.p, options.q);
      return {
        room: "Plan atelier / the duplicate guard",
        boundary:
          "Logic model · one source row · not rows fetched during compilation",
        nodes: [
          node(
            "Branch 1 · P",
            `P = ${options.p} · ${m.first ? "accept" : "skip"}`,
            "OWN ASSIGNMENT",
            m.first ? "blue" : "rose",
          ),
          node(
            "Branch 2 · guard",
            `Q = ${options.q} · LNNVL(P) = ${options.p !== "TRUE" ? "TRUE" : "FALSE"}`,
            "Q AND LNNVL(P)",
            m.second ? "teal" : "rose",
          ),
          node(
            `${Number(m.first) + Number(m.second)} copy from this row`,
            `Original P OR Q: ${m.original ? "qualifies" : "does not qualify"}`,
            "UNION ALL",
            "amber",
          ),
        ],
        links: [
          [0, 2],
          [1, 2],
        ],
        focus: [0, 1, 2],
      };
    }
    case "ore-cost":
      return {
        room: "CBO foundry / following nested work",
        boundary:
          "Binary correlation + recorded 10053 costs · not measured CPU time",
        nodes: [
          node(
            "Visit AND / OR",
            "kkoaccsqf → kkostTravLogSQAux",
            "RECURSIVE TRAVERSAL",
          ),
          node(
            "Nested subquery",
            options.scenario === "fixed"
              ? "Included in the original candidate"
              : "Omitted in this recorded base costing",
            "WEIGHTED EXECUTIONS × UNIT COST",
            options.scenario === "fixed" ? "teal" : "rose",
          ),
          node(
            number(c.base),
            "Original candidate · first-pass cost",
            "BASE COST",
            "amber",
          ),
          node(
            number(c.ore),
            "Expanded candidate · first-pass cost",
            "ORE COST",
            "teal",
          ),
        ],
        links: [
          [0, 1],
          [1, 2],
          [1, 3],
        ],
        focus: [0, 1, 2, 3],
      };
    case "ore-decision":
      return {
        room: "CBO foundry / the comparison desk",
        boundary:
          "Same first-pass decision · final plan cost 804,725 is a different stage",
        nodes: [
          node(number(c.base), "Original state", "BASE CANDIDATE", "blue"),
          node(number(c.ore), "Expanded state", "ORE CANDIDATE", "teal"),
          node(
            c.winner === "ore" ? "Choose ORE" : "Keep original",
            c.label,
            "RECORDED WINNER",
            "amber",
          ),
        ],
        links: [
          [0, 2],
          [1, 2],
        ],
        focus: [0, 1, 2],
      };
    case "ore-estimates":
      return {
        room: "CBO foundry / estimates and observations",
        boundary:
          "Diagnostic filter rows · not subquery starts or final result rows",
        nodes: [
          node(
            number(c.estimatedRows),
            "Diagnostic filter · prediction",
            "ESTIMATED ROWS",
            "blue",
          ),
          node(
            number(c.actualRows),
            "Diagnostic filter · observation",
            "ACTUAL ROWS",
            "teal",
          ),
          node(
            options.scenario === "histograms"
              ? "Closer estimate"
              : "Estimate is too high",
            "Statistics and costing are separate issues",
            "READ THE DIFFERENCE",
            "amber",
          ),
        ],
        links: [
          [0, 2],
          [1, 2],
        ],
        focus: [0, 1, 2],
      };
    case "ore-runtime": {
      const unit = {
        employeeCalls: "EMPLOYEE_ID calls",
        dateCalls: "CANONICAL_TO_DATE calls",
        starts: "Subquery starts",
        buffers: "Buffer gets",
        wallMs: "Full-fetch median · ms",
      }[options.metric];
      const format = (v: number) =>
        options.metric === "wallMs" ? v.toFixed(2) : number(v);
      return {
        room: "Execution gallery / measured work",
        boundary:
          "Recorded A/B lab · 220 result rows in both · not a live Oracle connection",
        nodes: [
          node(
            format(ORE_MEASUREMENTS.base[options.metric]),
            unit,
            "BASE EXECUTION",
            "blue",
          ),
          node(
            format(ORE_MEASUREMENTS.ore[options.metric]),
            unit,
            "ORE EXECUTION",
            "teal",
          ),
          node(
            "Same result · 220 rows",
            "Fewer function calls ≠ fewer buffer gets",
            "COMPARE THE WORK",
            "amber",
          ),
        ],
        links: [
          [0, 2],
          [1, 2],
        ],
        focus: [0, 1, 2],
      };
    }
    default:
      return null;
  }
}

export function interiorProgress(progress: number, immediate: boolean): number {
  return immediate
    ? Math.min(1, Math.max(0, 0.3 + progress * 0.7))
    : Math.min(1, Math.max(0, (progress - 0.2) / 0.8));
}

export function shouldEnterInterior(
  available: boolean,
  guided: boolean,
  mode: "auto" | "inside" | "outside",
  progress: number,
): boolean {
  return (
    available &&
    guided &&
    mode !== "outside" &&
    (mode === "inside" || progress >= 0.2)
  );
}
